/**
 * Network allowlist primitives.
 *
 * Centralizes the "is this URL/host on the LAN?" / "is this URL/host trusted?"
 * checks that previously lived inline in three places:
 *   - server `routes/proxy.ts:isUrlAllowed` (SSRF + LAN + MUNI allowlist)
 *   - server `utils/ip-validation.ts:validateIpAccess` (CIDR-based, IP_RESTRICTED)
 *   - client `routes/screen-viewer.tsx:isLocalNetworkUrl` (skip-the-proxy hint)
 *
 * The depth seam separates two orthogonal questions:
 *   - **Locality**: is this host on the LAN (private IP / loopback / localhost)?
 *   - **Trust**: is this host on a configured allowlist (e.g. `*.muni.cz`)?
 *
 * Security-policy concerns (SSRF blocklist, trusted bypass, NODE_ENV gates)
 * stay in the server proxy layer. This module is environment-agnostic and
 * safe to call from the browser.
 */
import { isIpInRange } from './utils.js';
// ============================================================================
// Locality: LAN / loopback / link-local
// ============================================================================
/**
 * Cloud-metadata endpoints and well-known internal hostnames that must never
 * be reachable from a proxied request. SSRF surface.
 */
export const SSRF_BLOCKED_HOSTS = [
    '169.254.169.254', // AWS/GCP IMDS
    'metadata.google.internal',
    'metadata.internal',
    'localhost',
    '127.0.0.1',
    '::1',
    '0.0.0.0',
];
/** RFC1918 + loopback + link-local hostname patterns. */
export const PRIVATE_NETWORK_PATTERNS = [
    /^127\./, // loopback
    /^10\./, // RFC 1918
    /^172\.(1[6-9]|2\d|3[01])\./, // RFC 1918
    /^192\.168\./, // RFC 1918
    /^169\.254\./, // IPv4 link-local — cloud metadata surface incl. AWS/GCP IMDS
    // and the ECS task-role credential endpoint 169.254.170.2 (SSRF → cred theft).
    // Keep this ahead of the CIDR allowlist so no SOURCE_URL_ALLOWLIST entry can
    // re-open it (matches this module's stated guarantee).
    /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./, // RFC 6598 CGNAT shared space (100.64.0.0/10)
    /^0\.0\.0\.0$/,
    /^\[?::1\]?$/, // IPv6 loopback
    /^\[?fe80:/i, // IPv6 link-local
];
/**
 * Canonicalize a host for comparison: trim, lowercase, and strip one trailing
 * root dot (every resolver treats `localhost.` as `localhost`).
 *
 * The WHATWG URL parser already does this — but ONLY for *special* schemes
 * (http/https/ws/…). **Non-special schemes (`rtsp:`, `ndi:`) keep the host
 * verbatim**, and callers passing a raw string get no normalization at all.
 * Both kinds reach the checks below, so normalize here rather than depending on
 * an unwritten "caller already normalized it" contract.
 */
export function canonicalizeHost(host) {
    const lowered = host.trim().toLowerCase();
    return lowered.endsWith('.') ? lowered.slice(0, -1) : lowered;
}
/**
 * True when the host is a private-network IP (RFC1918), loopback, IPv6
 * link-local, the literal `localhost`, or an `*.localhost` subdomain.
 *
 * The `*.localhost` clause follows RFC 6761 §6.3, which reserves the
 * `localhost.` TLD (and any subdomain of it) to resolve to loopback. This
 * matters in dev where developers front services behind names like
 * `polygraf.localhost`, `inside.localhost`, etc. via Caddy.
 */
export function isPrivateNetworkHost(host) {
    const canonical = canonicalizeHost(host);
    if (canonical === 'localhost')
        return true;
    if (canonical.endsWith('.localhost'))
        return true;
    return PRIVATE_NETWORK_PATTERNS.some((p) => p.test(canonical));
}
/**
 * True when a browser will fetch `http://<host>` from an `https` page WITHOUT
 * mixed-content blocking, because the host is a *potentially trustworthy*
 * origin: W3C Secure Contexts §3.1 grants that status to loopback (127.0.0.0/8,
 * `::1`) and, per RFC 6761 §6.3, the reserved `localhost` TLD.
 *
 * Deliberately NARROWER than {@link isPrivateNetworkHost}: RFC1918 and CGNAT
 * addresses are NOT potentially trustworthy, so `http://192.168.1.50/cam.jpg`
 * on an https page IS blocked. Conflating the two is the bug this exists to
 * prevent — see `shouldProxyAdHocSource`.
 */
export function isBrowserTrustworthyHost(host) {
    const canonical = canonicalizeHost(host);
    if (canonical === 'localhost')
        return true;
    if (canonical.endsWith('.localhost'))
        return true;
    return /^127\./.test(canonical) || /^\[?::1\]?$/.test(canonical);
}
/**
 * True when the URL points at a private-network host. Returns false for
 * malformed URLs.
 */
export function isLanUrl(url) {
    try {
        return isPrivateNetworkHost(new URL(url).hostname);
    }
    catch {
        return false;
    }
}
/**
 * True when the host is an SSRF blocklist target.
 *
 * Canonicalizes first: the list is exact-match, so without normalization
 * `LOCALHOST` / `localhost.` / `METADATA.GOOGLE.INTERNAL` slip straight past it
 * — which is exactly what happens for `rtsp://` / `ndi://` endpoints, whose
 * non-special scheme leaves `URL.hostname` un-lowercased.
 */
export function isSsrfBlockedHost(host) {
    return SSRF_BLOCKED_HOSTS.includes(canonicalizeHost(host));
}
/** Domains operated by Masaryk University that Polygraf considers trusted. */
export const MUNI_TRUSTED_DOMAINS = ['*.muni.cz', '*.teiresias.muni.cz'];
/**
 * Public IPv4 ranges operated by Masaryk University. Campus AV hardware
 * (cameras, encoders) is typically addressed by bare IP on this range, so it
 * must be trustable without a hostname (UAT B6, MP-218).
 */
export const MUNI_TRUSTED_CIDRS = ['147.251.0.0/16'];
export const DEFAULT_ALLOWLIST_CONFIG = {
    trustedDomainPatterns: MUNI_TRUSTED_DOMAINS,
    allowedCidrs: MUNI_TRUSTED_CIDRS,
};
/**
 * True when the URL's hostname matches one of the configured trusted-domain
 * glob patterns.
 */
export function isTrustedDomain(url, config = DEFAULT_ALLOWLIST_CONFIG) {
    try {
        const host = new URL(url).hostname;
        return config.trustedDomainPatterns.some((pattern) => globMatchesHost(host, pattern));
    }
    catch {
        return false;
    }
}
/**
 * True when the URL's host is trusted: either the hostname matches a
 * trusted-domain glob, or it is an IPv4 literal inside one of the allowed
 * CIDR ranges.
 *
 * IPv6 literals never match a CIDR (`isIpInRange` is IPv4-only — fail
 * closed), and a domain name can never satisfy the CIDR branch. This is a
 * trust primitive only: SSRF blocklist and private-range policy stay in the
 * server gate (`routes/proxy.ts:isUrlAllowed`), which runs them BEFORE any
 * trust check.
 */
export function isTrustedHost(url, config = DEFAULT_ALLOWLIST_CONFIG) {
    if (isTrustedDomain(url, config))
        return true;
    const cidrs = config.allowedCidrs ?? [];
    if (cidrs.length === 0)
        return false;
    try {
        const host = new URL(url).hostname;
        return cidrs.some((cidr) => isIpInRange(host, cidr));
    }
    catch {
        return false;
    }
}
/** Match a hostname against a `*`-glob pattern (e.g. `*.muni.cz`). Case-insensitive. */
export function globMatchesHost(host, pattern) {
    const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
    const regex = new RegExp(`^${escaped}$`, 'i');
    return regex.test(host);
}
//# sourceMappingURL=network-allowlist.js.map